import { BuilderComponent } from "@builder.io/react";
import { BuilderContent } from "@builder.io/sdk";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect } from "react";

import { parallel } from "@/utils/promises";
import serverSideTranslations from "@/utils/serverSideTranslations";

import EVENTS from "@/analytics/events";
import { trackPageLoad } from "@/analytics/tracking";

import { init } from "@/builder";
import { BuilderModel } from "@/builder/types";

import DefaultLayout from "@/layouts/default";

import {
    getBuilderBannerForCategory,
    getBuilderMessages,
    getBuilderNavLinks,
} from "@/pages/api/builder/builder";
import { fetchCategory, getParentCategory } from "@/pages/api/store-directory/categories";
import { getHeaderNavigation, getFooterNavigation } from "@/pages/api/store-directory/navigation";

import StoreDirectory from "@/components/store-directory/store-directory";

import { CategoryProps } from "@/interfaces/category-interface";
import { ChunkedNav, GenericNavItem } from "@/interfaces/navigation-interface";

import { Locale } from "@/types/locale";
import { GetServerSideProps } from "@/types/page";

interface LocaleProps {
    locale: Locale;
}

type NavProps = {
    headerNavigationItems: ChunkedNav;
    footerLocalNavItems: ChunkedNav;
    footerRegionalNavItems: Array<Array<GenericNavItem>>;
};

type ControlHeaderProps = {
    parentCategory: CategoryProps;
    currentCategory: CategoryProps;
};

type OwnProps = {
    builderMessages: Array<BuilderContent>;
    builderNavLinks: Array<BuilderContent>;
    builderBanner: BuilderContent | null;
};

type Props = OwnProps & NavProps & LocaleProps & ControlHeaderProps;

type Params = {
    slug: string;
};

init(BuilderModel.MESSAGE);
init(BuilderModel.BANNER);

export const getServerSideProps: GetServerSideProps<Props, Params> = async ({
    locale,
    resolvedUrl,
    params,
}) => {
    if (locale === "global") {
        return {
            notFound: true,
        };
    }
    const slug = params.slug;

    const currentCategory = await fetchCategory(locale, slug);
    if (!currentCategory) {
        return {
            notFound: true,
        };
    }

    const parallelProps = await parallel({
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        builderMessages: () => getBuilderMessages(locale, { url: resolvedUrl }),
        parentCategory: () => getParentCategory(locale, currentCategory.parentId),
        builderNavLinks: () => getBuilderNavLinks(locale),
        builderBanner: () => getBuilderBannerForCategory(locale, slug),
        _nextI18Next: () => serverSideTranslations(locale),
    });

    return {
        props: {
            locale,
            slug,
            currentCategory,
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            ...parallelProps,
        },
    };
};

const Category = ({
    builderMessages,
    builderNavLinks,
    builderBanner,
    locale,
    headerNavigationItems,
    footerLocalNavItems,
    footerRegionalNavItems,
    parentCategory,
    currentCategory,
}: Props): React.ReactNode => {
    const router = useRouter();
    const { t } = useTranslation();

    useEffect(() => {
        trackPageLoad(router.asPath, locale, EVENTS.SHOP_DIRECTORY_CATEGORY_LIST_VIEW, {
            categoryName: currentCategory.slug,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <DefaultLayout
            title={
                currentCategory.metaTitle ||
                t("category.metaTitle", { categoryName: currentCategory.name })
            }
            description={
                currentCategory.metaDescription ||
                t("category.metaDescription", {
                    categoryName: currentCategory.name,
                })
            }
            currentLocale={locale}
            headerNavigationItems={headerNavigationItems}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            builderNavLinks={builderNavLinks}
            builderMessages={builderMessages}
        >
            <BuilderComponent model={BuilderModel.BANNER} content={builderBanner} />
            <StoreDirectory parentCategory={parentCategory} currentCategory={currentCategory} />
        </DefaultLayout>
    );
};

export default Category;
